import React from 'react';

import { createEditorValue, createSheetEditorValue } from '@gitbook/doc-core';
import type { RevisionPageDocument } from '@gitbook/hive-core';
import {
    HiveWorkingDocumentProvider,
    useHiveBranch,
    useHiveClient,
    useHiveComputedDocument,
    useHiveWorkingDocument,
    useHiveWorkingDocumentValue,
} from '@gitbook/hive-react';
import type { Value } from '@gitbook/slate';
import { getSuspense } from '@gitbook/util-suspense-cache';
import { assertIsDefined, hasMandateProp, type MandateProps } from '@gitbook/util-ts';

type WithPageDocumentProps<Page extends RevisionPageDocument = RevisionPageDocument> = {
    spaceId: string;
    page: Page;
    readOnly?: boolean;
    children: ({ value }: { value: Value }) => React.ReactNode;
};

/**
 * Utility to fetch the document for a page.
 */
export function WithPageDocument(props: WithPageDocumentProps) {
    const { page, readOnly } = props;

    const hiveBranch = useHiveBranch();

    if (hasMandateProp(page, 'computed')) {
        return <WithComputedPageDocument {...props} page={page} />;
    }

    if (!hiveBranch) {
        return <WithFrozenPageDocument {...props} />;
    }

    return (
        <HiveWorkingDocumentProvider
            containerType="pages"
            containerId={page.id}
            readOnly={readOnly}
        >
            <WithLivePageDocument {...props} />
        </HiveWorkingDocumentProvider>
    );
}

function WithFrozenPageDocument(props: WithPageDocumentProps) {
    const { spaceId, page, children } = props;

    const hiveClient = useHiveClient();
    const document = page.document
        ? getSuspense(hiveClient.store.getDocument(spaceId, page.document))
        : undefined;

    const value = React.useMemo(() => {
        return document ? createEditorValue(document) : createSheetEditorValue(page.id, 'document');
    }, [document, page.id]);

    return children({ value });
}

function WithComputedPageDocument(
    props: WithPageDocumentProps<MandateProps<RevisionPageDocument, 'computed'>>
) {
    const { spaceId, page, children } = props;

    const computedDocument = useHiveComputedDocument({
        contentId: spaceId,
        computedSeed: page.computed.seed,
        computedSource: page.computed.source,
    });
    const value = React.useMemo(() => createEditorValue(computedDocument), [computedDocument]);

    return children({ value });
}

function WithLivePageDocument(props: WithPageDocumentProps) {
    const { children } = props;

    const hivePage = useHiveWorkingDocument();
    assertIsDefined(hivePage);
    const [value] = useHiveWorkingDocumentValue(hivePage);

    return children({ value });
}
